
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchScheduleAlerts } from '../services/alertFetchService';
import { resolveAlert } from '../services/alertResolveService';
import { generateScheduleAlerts } from '../services/alertGenerateService';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { startOfWeek, format, endOfWeek, addDays } from 'date-fns';

// Define date range types for the schedule alerts
export type DateRangeType = 'week' | 'twoWeeks' | 'month' | 'custom';

export const useScheduleAlerts = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState<Date>(new Date());
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>('week');
  const [customDateRange, setCustomDateRange] = useState<[Date | undefined, Date | undefined]>([
    undefined, undefined
  ]);
  
  // Calculate the actual start and end dates based on selection
  const getDateRange = () => {
    const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 1 });
    
    switch (dateRangeType) {
      case 'week':
        return {
          startDate: weekStart,
          endDate: endOfWeek(weekStart, { weekStartsOn: 1 })
        };
      case 'twoWeeks':
        return {
          startDate: weekStart,
          endDate: addDays(endOfWeek(weekStart, { weekStartsOn: 1 }), 7)
        };
      case 'month':
        return {
          startDate: weekStart,
          endDate: addDays(weekStart, 30)
        };
      case 'custom':
        return {
          startDate: customDateRange[0] || weekStart,
          endDate: customDateRange[1] || endOfWeek(weekStart, { weekStartsOn: 1 })
        };
      default:
        return {
          startDate: weekStart,
          endDate: endOfWeek(weekStart, { weekStartsOn: 1 })
        };
    }
  };
  
  const { startDate, endDate } = getDateRange();
  
  // Fetch alerts with selected date range
  const { 
    data: alerts = [], 
    isLoading,
    isError,
    refetch
  } = useQuery({
    queryKey: ['schedule-alerts', startDate.toISOString(), endDate.toISOString(), selectedTeamId],
    queryFn: () => fetchScheduleAlerts(startDate, endDate, selectedTeamId)
  });
  
  // Resolve alert mutation
  const resolveMutation = useMutation({
    mutationFn: resolveAlert,
    onSuccess: () => {
      toast.success(t('alertResolved') || 'Alert marked as resolved');
      queryClient.invalidateQueries({ queryKey: ['schedule-alerts'] });
    },
    onError: (error) => {
      toast.error(t('errorResolvingAlert') || 'Failed to resolve alert');
      console.error('Error resolving alert:', error);
    }
  });
  
  // Handle resolving an alert
  const handleResolveAlert = async (alertId: string) => {
    resolveMutation.mutate(alertId);
  };
  
  // Handle week selection
  const handleSelectWeek = (date: Date) => {
    setSelectedWeek(date);
  };

  // Handle team selection
  const handleSelectTeam = (teamId: string | null) => {
    setSelectedTeamId(teamId);
  };
  
  // Handle date range type selection
  const handleSelectDateRangeType = (type: DateRangeType) => {
    setDateRangeType(type);
  };
  
  // Handle custom date range selection
  const handleSelectCustomDateRange = (range: [Date | undefined, Date | undefined]) => {
    setCustomDateRange(range);
    
    // If we have a valid range, automatically switch to custom mode
    if (range[0] && range[1]) {
      setDateRangeType('custom');
    }
  };
  
  // Generate new alerts
  const handleGenerateAlerts = async () => {
    setIsGenerating(true);
    try {
      await generateScheduleAlerts(startDate, selectedTeamId);
      toast.success(t('alertsGenerated') || 'Schedule alerts have been generated');
      refetch();
    } catch (error) {
      toast.error(t('errorGeneratingAlerts') || 'Failed to generate alerts');
      console.error('Error generating alerts:', error);
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Filter alerts by selected team if a team is selected
  const filteredAlerts = selectedTeamId 
    ? alerts.filter(alert => alert.team_id === selectedTeamId)
    : alerts;
    
  // Active alerts (not resolved)
  const activeAlerts = filteredAlerts.filter(alert => !alert.is_resolved);
  
  // Resolved alerts
  const resolvedAlerts = filteredAlerts.filter(alert => alert.is_resolved);
  
  return {
    alerts: filteredAlerts,
    activeAlerts,
    resolvedAlerts,
    isLoading,
    isError,
    isGenerating,
    selectedWeek,
    selectedTeamId,
    dateRangeType,
    customDateRange,
    startDate,
    endDate,
    handleResolveAlert,
    handleGenerateAlerts,
    handleSelectWeek,
    handleSelectTeam,
    handleSelectDateRangeType,
    handleSelectCustomDateRange,
    refetch
  };
};
